import { useQuery } from "react-query";
import { z } from "zod";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { PuckPage } from "./useCreatePuckPage";
import { useIsCmsEnabled } from "./useIsCmsEnabled";
import i18next from "i18next";

export const getPuckPageQueryKey = "getPuckPage";

type GetPuckPageOptions = {
    slug: string;
    enabled?: boolean;
};

export const useGetPuckPage = (options: GetPuckPageOptions) => {
    const validatedOptions = GetPuckPageOptionsSchema.parse(options);
    const { slug, enabled = true } = validatedOptions;
    const lang = i18next.language;
    const isCmsEnabled = useIsCmsEnabled();

    const url = `/api/cms/puck/page/${slug}`;

    const query = useQuery({
        queryKey: [getPuckPageQueryKey, slug, lang],
        enabled: enabled && slug !== "" && isCmsEnabled,
        queryFn: async () => {
            const response = await loggedInApiCall.get<PuckPage>(url);

            return PuckPageResponseSchema.parse(response.data);
        },
        onError: (error: unknown) => {
            if (error instanceof Error) {
                console.error("Error fetching Puck page:", error.message);
            } else {
                console.error("Unknown error fetching Puck page:", error);
            }
        },
    });

    return {
        page: query.data,
        ...query,
    };
};

export const GetPuckPageOptionsSchema: z.ZodSchema<GetPuckPageOptions> = z.object({
    slug: z.string(),
    enabled: z.boolean().optional(),
});

export const PuckPageResponseSchema: z.ZodSchema<PuckPage> = z.object({
    slug: z.string(),
    title: z.string(),
    content: z.string(),
    locale: z.string(),
    createdAt: z.string(),
    updatedAt: z.string(),
    url: z.string().optional(),
    pageType: z.enum(["CMS", "PDF"]).optional(),
    status: z.enum(["active", "draft"]).optional(),
    allowedRoles: z.array(z.string()).nullish(),
    sectors: z.array(z.string()).nullish(),
});
